import { stringifyParams } from "@bloomberg/intercom";
import type { PersonalizationAsset } from "@foundation/personalization";

import { DEFAULT_PAGE_NUMBER, DEFAULT_STORIES_QUERY_LIMIT } from "~constants";
import type { AutofillData, CuratedStory } from "~types/types";
import type { Brand, StoryType } from "~types/wardenclyffe";

export async function fetchExpandedStoriesFromApi<T>(
    personalizationAssets: PersonalizationAsset[],
    autofillData?: AutofillData
): Promise<T[]> {
    const storyIds = personalizationAssets.map(({ assetID }) => assetID);

    const storiesResponse = await fetch(
        `/lineup-next/api/stories/${storyIds.join(
            ","
        )}?autofillData=${JSON.stringify(autofillData)}`
    );

    if (!storiesResponse.ok) {
        throw new Error(
            `Error when fetching from api/stories: ${await storiesResponse.text()}`
        );
    }

    const storiesJson: PromiseFulfilledResult<T>[] =
        await storiesResponse.json();

    return storiesJson
        .filter(({ status }) => status === "fulfilled")
        .map((item) => item.value);
}

export async function fetchLatestStoriesFromApi(filter: string) {
    const storiesResponse = await fetch(
        `/lineup-next/api/latest-stories?filter=${filter}`
    );

    if (!storiesResponse.ok) {
        throw new Error(
            `Error when fetching from api/latest-stories: ${storiesResponse.text}`
        );
    }

    return storiesResponse.json();
}

export type FetchStoriesParams = {
    limit?: number;
    brand?: Brand;
    pageNumber?: number;
    types?: StoryType[];
    id?: string;
    publishedAt?: string;
};

export async function fetchStoriesFromApi({
    limit = DEFAULT_STORIES_QUERY_LIMIT,
    pageNumber = DEFAULT_PAGE_NUMBER,
    ...remainingParams
}: FetchStoriesParams) {
    try {
        const URLFetchStoriesParams = stringifyParams({
            limit,
            pageNumber,
            ...remainingParams,
        });
        const storiesResponse = await fetch(
            `/lineup-next/api/stories?${URLFetchStoriesParams.toString()}`
        );

        if (!storiesResponse.ok) {
            throw new Error(
                `Error when fetching from api/stories: ${storiesResponse.text()}`
            );
        }

        return (await storiesResponse.json()) as CuratedStory[];
    } catch (error) {
        throw new Error(`Error when fetching from api/stories: ${error}`);
    }
}

export async function fetchStoriesForMostActiveCompaniesFromApi({
    limit = DEFAULT_STORIES_QUERY_LIMIT,
    pageNumber = DEFAULT_PAGE_NUMBER,
}: FetchStoriesParams) {
    try {
        const storiesResponse = await fetch(
            `/lineup-next/api/market-movers/stories?limit=${limit}${
                pageNumber ? `&pageNumber=${pageNumber}` : ""
            }`
        );

        if (!storiesResponse.ok) {
            throw new Error(
                `Error when fetching from api/market-movers/stories: ${storiesResponse.text()}`
            );
        }

        return (await storiesResponse.json()) as CuratedStory[];
    } catch (error) {
        throw new Error(`Error when fetching from api/stories: ${error}`);
    }
}
